import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiInfo } from "react-icons/fi";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Loader from "../components/common/Loader";
import Button from "../components/common/Button";
import BookingForm from "../components/booking/BookingForm";
import BookingSummary from "../components/booking/BookingSummary";
import * as serviceService from "../services/serviceService";
import * as bookingService from "../services/bookingService";
import * as availabilityService from "../services/availabilityService";
import { hasValidSession } from "../services/authService";
import {
  AUTO_MOBILE_PARTY_SIZE_THRESHOLD,
  BOOKING_OWNER_TYPE,
  HENNA_TYPE_OPTIONS,
  HENNA_TYPES,
  JAGUAR_HENNA_DISCLAIMER,
  JAGUAR_HENNA_EXTRA_PRICE,
  MOBILE_MODE_DESCRIPTION,
  MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
  SERVICE_MODE,
  STUDIO_MODE_DESCRIPTION,
} from "../utils/constants";
import { calculatePartySize, validateBookingPayload } from "../utils/validators";
import { formatDate, formatTime } from "../utils/formatDate";
import formatCurrency from "../utils/formatCurrency";

const STUDIO_LOCATION = "The artist will email or text the studio address within 48 hours before your appointment.";

const initialFormData = {
  serviceId: "",
  hennaType: "",
  bookingDate: "",
  startTime: "",
  endTime: "",
  numOfAdults: 1,
  numOfChildren: 0,
  partySize: 1,
  notes: "",
  serviceMode: SERVICE_MODE.STUDIO,
  locationNotes: "",
  onsiteAddress: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
  },
  guest: {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  },
};

const safeCallGetServices = async () => {
  if (typeof serviceService.getAllServices === "function") return serviceService.getAllServices();
  if (typeof serviceService.getServices === "function") return serviceService.getServices();
  if (typeof serviceService.fetchServices === "function") return serviceService.fetchServices();
  throw new Error("Service list method was not found in serviceService.");
};

const safeCallValidateAvailability = async ({ bookingDate, startTime, endTime }) => {
  if (typeof availabilityService.validateAvailability === "function") {
    return availabilityService.validateAvailability(bookingDate, startTime, endTime);
  }
  if (typeof availabilityService.checkAvailability === "function") {
    return availabilityService.checkAvailability(bookingDate, startTime, endTime);
  }
  if (typeof availabilityService.validateBookingTime === "function") {
    return availabilityService.validateBookingTime(bookingDate, startTime, endTime);
  }
  throw new Error("Availability validation method was not found in availabilityService.");
};

const safeCallGetAvailabilityBetween = async (start, end) => {
  if (typeof availabilityService.getAvailabilityBetweenDates === "function") {
    return availabilityService.getAvailabilityBetweenDates(start, end);
  }
  throw new Error("Availability between dates method was not found.");
};

const safeCallGetAvailabilityByDate = async (date) => {
  if (typeof availabilityService.getAvailabilityByDate === "function") {
    return availabilityService.getAvailabilityByDate(date);
  }
  throw new Error("Availability by date method was not found.");
};

const safeCallGetBookingsByDate = async (date) => {
  if (typeof bookingService.getBookingsByDate === "function") {
    return bookingService.getBookingsByDate(date);
  }
  throw new Error("Bookings by date method was not found.");
};

const safeCallCreateUserBooking = async (payload) => {
  if (typeof bookingService.createUserBooking === "function") return bookingService.createUserBooking(payload);
  if (typeof bookingService.bookAsUser === "function") return bookingService.bookAsUser(payload);
  if (typeof bookingService.createBookingForUser === "function") return bookingService.createBookingForUser(payload);
  throw new Error("User booking method was not found in bookingService.");
};

const safeCallCreateGuestBooking = async (payload) => {
  if (typeof bookingService.createGuestBooking === "function") return bookingService.createGuestBooking(payload);
  if (typeof bookingService.bookAsGuest === "function") return bookingService.bookAsGuest(payload);
  if (typeof bookingService.createBookingForGuest === "function") return bookingService.createBookingForGuest(payload);
  throw new Error("Guest booking method was not found in bookingService.");
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const formatDateToApi = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const timeToMinutes = (timeString) => {
  const [h, m] = String(timeString).slice(0, 5).split(":").map(Number);
  return h * 60 + m;
};

const minutesToTime = (minutes) => {
  const h = String(Math.floor(minutes / 60)).padStart(2, "0");
  const m = String(minutes % 60).padStart(2, "0");
  return `${h}:${m}`;
};

const overlaps = (startA, endA, startB, endB) => startA < endB && endA > startB;

const buildAddressText = (onsiteAddress) => {
  if (!onsiteAddress) return "";

  return [
    onsiteAddress.line1,
    onsiteAddress.line2,
    onsiteAddress.city,
    onsiteAddress.state,
    onsiteAddress.zip,
  ]
    .filter(Boolean)
    .join(", ");
};

const getDefaultHennaTypeForService = (service) => {
  const existingHennaType = service?.hennaType || "";
  return HENNA_TYPE_OPTIONS.includes(existingHennaType)
    ? existingHennaType
    : HENNA_TYPES.RED_NATURAL;
};

const getAutoServiceMode = (partySize) =>
  Number(partySize) >= AUTO_MOBILE_PARTY_SIZE_THRESHOLD
    ? SERVICE_MODE.MOBILE
    : SERVICE_MODE.STUDIO;

const emptyOnsiteAddress = () => ({
  line1: "",
  line2: "",
  city: "",
  state: "",
  zip: "",
});

const Booking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [services, setServices] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [availabilityMessage, setAvailabilityMessage] = useState("");
  const [bookingOwnerType, setBookingOwnerType] = useState(BOOKING_OWNER_TYPE.USER);
  const [isAuthenticated, setIsAuthenticated] = useState(hasValidSession());

  useEffect(() => {
    const syncAuthState = () => {
      setIsAuthenticated(hasValidSession());
    };

    syncAuthState();

    window.addEventListener("focus", syncAuthState);
    document.addEventListener("visibilitychange", syncAuthState);

    return () => {
      window.removeEventListener("focus", syncAuthState);
      document.removeEventListener("visibilitychange", syncAuthState);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setBookingOwnerType(BOOKING_OWNER_TYPE.GUEST);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const loadServicesAndAvailableDates = async () => {
      try {
        setLoadingServices(true);
        setPageError("");

        const [servicesData, availabilityData] = await Promise.all([
          safeCallGetServices(),
          safeCallGetAvailabilityBetween(
            formatDateToApi(new Date()),
            formatDateToApi(addDays(new Date(), 60))
          ),
        ]);

        if (servicesData?.statusCode && servicesData.statusCode !== 200) {
          throw new Error(servicesData.message || "Failed to load services.");
        }

        if (availabilityData?.statusCode && availabilityData.statusCode !== 200) {
          throw new Error(availabilityData.message || "Failed to load availability.");
        }

        const serviceList = Array.isArray(servicesData?.serviceList) ? servicesData.serviceList : [];
        const activeServices = serviceList.filter((service) => service?.active !== false);
        setServices(activeServices);

        const rules = Array.isArray(availabilityData?.availabilityRuleList)
          ? availabilityData.availabilityRuleList
          : [];

        const uniqueDates = [
          ...new Set(
            rules
              .filter((rule) => rule?.isActive !== false && rule?.date)
              .map((rule) => rule.date)
          ),
        ];

        setAvailableDates(uniqueDates);

        const serviceIdFromQuery = searchParams.get("serviceId");
        if (serviceIdFromQuery) {
          const matched = activeServices.find(
            (service) => Number(service.id) === Number(serviceIdFromQuery)
          );
          if (matched) {
            setFormData((prev) => ({
              ...prev,
              serviceId: String(matched.id),
              hennaType: getDefaultHennaTypeForService(matched),
            }));
          }
        }
      } catch (err) {
        setPageError(err?.message || "Unable to load booking services.");
      } finally {
        setLoadingServices(false);
      }
    };

    loadServicesAndAvailableDates();
  }, [searchParams]);

  const selectedService = useMemo(() => {
    return services.find((service) => Number(service.id) === Number(formData.serviceId)) || null;
  }, [services, formData.serviceId]);

  const calculatedPartySize = useMemo(() => {
    return calculatePartySize(formData.numOfAdults, formData.numOfChildren);
  }, [formData.numOfAdults, formData.numOfChildren]);

  useEffect(() => {
    const nextMode = getAutoServiceMode(calculatedPartySize);

    setFormData((prev) => {
      const shouldClearLocation =
        nextMode === SERVICE_MODE.STUDIO &&
        (prev.locationNotes ||
          prev.onsiteAddress.line1 ||
          prev.onsiteAddress.line2 ||
          prev.onsiteAddress.city ||
          prev.onsiteAddress.state ||
          prev.onsiteAddress.zip);

      if (prev.serviceMode === nextMode && !shouldClearLocation) {
        return prev;
      }

      return {
        ...prev,
        serviceMode: nextMode,
        locationNotes: nextMode === SERVICE_MODE.STUDIO ? "" : prev.locationNotes,
        onsiteAddress: nextMode === SERVICE_MODE.STUDIO ? emptyOnsiteAddress() : prev.onsiteAddress,
      };
    });

    if (nextMode === SERVICE_MODE.STUDIO) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.locationNotes;
        delete nextErrors.onsiteAddress;
        delete nextErrors.serviceMode;
        return nextErrors;
      });
    }
  }, [calculatedPartySize]);

  const calculatedBookingDurationMinutes = useMemo(() => {
    const baseDuration = Number(selectedService?.serviceDurationMinutes) || 0;
    const adults = Number(formData.numOfAdults) || 0;
    const children = Number(formData.numOfChildren) || 0;

    const adultDuration = baseDuration * adults;
    const childDuration = baseDuration * 0.5 * children;

    return Math.round(adultDuration + childDuration);
  }, [selectedService, formData.numOfAdults, formData.numOfChildren]);

  const calculatedBookingPrice = useMemo(() => {
    const basePrice = Number(selectedService?.servicePrice) || 0;
    const adults = Number(formData.numOfAdults) || 0;
    const children = Number(formData.numOfChildren) || 0;

    const adultPrice = basePrice * adults;
    const childPrice = basePrice * 0.5 * children;
    const subtotal = adultPrice + childPrice;

    return formData.hennaType === HENNA_TYPES.JAGUAR
      ? subtotal + JAGUAR_HENNA_EXTRA_PRICE
      : subtotal;
  }, [selectedService, formData.numOfAdults, formData.numOfChildren, formData.hennaType]);

  useEffect(() => {
    const loadSlotsForSelectedDate = async () => {
      if (!formData.bookingDate || !selectedService?.serviceDurationMinutes) {
        setAvailableSlots([]);
        return;
      }

      try {
        setSlotsLoading(true);
        setAvailabilityMessage("");

        const [availabilityData, bookingsData] = await Promise.all([
          safeCallGetAvailabilityByDate(formData.bookingDate),
          safeCallGetBookingsByDate(formData.bookingDate),
        ]);

        if (availabilityData?.statusCode && availabilityData.statusCode !== 200) {
          throw new Error(availabilityData.message || "Failed to load date availability.");
        }

        if (bookingsData?.statusCode && bookingsData.statusCode !== 200) {
          throw new Error(bookingsData.message || "Failed to load bookings for selected date.");
        }

        const rules = Array.isArray(availabilityData?.availabilityRuleList)
          ? availabilityData.availabilityRuleList.filter((rule) => rule?.isActive !== false)
          : [];

        const bookings = Array.isArray(bookingsData?.bookingList)
          ? bookingsData.bookingList.filter((booking) => booking?.status !== "CANCELED")
          : [];

        const duration = Number(calculatedBookingDurationMinutes);
        const slots = [];

        if (!duration || duration <= 0) {
          setAvailableSlots([]);
          return;
        }

        rules.forEach((rule) => {
          const ruleStart = timeToMinutes(rule.startTime);
          const ruleEnd = timeToMinutes(rule.endTime);

          for (let start = ruleStart; start + duration <= ruleEnd; start += 15) {
            const end = start + duration;

            const isBlocked = bookings.some((booking) => {
              const bookingStart = timeToMinutes(booking.startTime);
              const bookingEnd = timeToMinutes(booking.endTime);
              return overlaps(start, end, bookingStart, bookingEnd);
            });

            if (!isBlocked) {
              slots.push({
                startTime: minutesToTime(start),
                endTime: minutesToTime(end),
              });
            }
          }
        });

        setAvailableSlots(slots);

        if (formData.startTime) {
          const matchingStartSlot = slots.find(
            (slot) => slot.startTime === formData.startTime
          );

          if (matchingStartSlot) {
            setFormData((prev) => ({
              ...prev,
              startTime: matchingStartSlot.startTime,
              endTime: matchingStartSlot.endTime,
            }));
          } else {
            setFormData((prev) => ({
              ...prev,
              startTime: "",
              endTime: "",
            }));
          }
        }
      } catch (err) {
        setAvailableSlots([]);
        setAvailabilityMessage(err?.message || "Unable to load available time slots.");
      } finally {
        setSlotsLoading(false);
      }
    };

    loadSlotsForSelectedDate();
  }, [formData.bookingDate, selectedService, calculatedBookingDurationMinutes, formData.startTime]);

  const resetMessages = () => {
    setPageError("");
    setAvailabilityMessage("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    resetMessages();

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => {
        const updated = {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value,
          },
        };
        updated.partySize = calculatePartySize(updated.numOfAdults, updated.numOfChildren);
        updated.serviceMode = getAutoServiceMode(updated.partySize);

        if (updated.serviceMode === SERVICE_MODE.STUDIO) {
          updated.locationNotes = "";
          updated.onsiteAddress = emptyOnsiteAddress();
        }

        return updated;
      });
    } else {
      setFormData((prev) => {
        const updated = {
          ...prev,
          [name]:
            name === "numOfAdults" || name === "numOfChildren"
              ? Math.max(0, Number(value))
              : value,
        };

        updated.partySize = calculatePartySize(updated.numOfAdults, updated.numOfChildren);
        updated.serviceMode = getAutoServiceMode(updated.partySize);

        if (name === "bookingDate" || name === "serviceId") {
          updated.startTime = "";
          updated.endTime = "";
        }

        if (name === "serviceId") {
          const nextService = services.find(
            (service) => Number(service.id) === Number(value)
          );

          updated.hennaType = nextService
            ? getDefaultHennaTypeForService(nextService)
            : "";
          setAvailableSlots([]);
        }

        if (updated.serviceMode === SERVICE_MODE.STUDIO) {
          updated.locationNotes = "";
          updated.onsiteAddress = emptyOnsiteAddress();
        }

        return updated;
      });
    }

    setErrors((prev) => {
      const next = { ...prev };

      if (name.includes(".")) {
        const [parent, child] = name.split(".");
        if (parent === "onsiteAddress" && next.onsiteAddress) {
          next.onsiteAddress = {
            ...next.onsiteAddress,
            [child]: "",
          };
        } else {
          delete next[name];
        }
      } else {
        delete next[name];
      }

      delete next.startTime;
      delete next.endTime;
      delete next.bookingDate;
      delete next.serviceId;
      delete next.serviceMode;

      return next;
    });
  };

  const handleSelectSlot = (slot) => {
    resetMessages();
    setFormData((prev) => ({
      ...prev,
      startTime: slot.startTime,
      endTime: slot.endTime,
    }));
    setErrors((prev) => ({
      ...prev,
      startTime: "",
      endTime: "",
    }));
  };

  const buildPayload = () => {
    const resolvedEndTime =
      formData.startTime && calculatedBookingDurationMinutes > 0
        ? minutesToTime(timeToMinutes(formData.startTime) + calculatedBookingDurationMinutes)
        : formData.endTime;

    const resolvedServiceMode = getAutoServiceMode(calculatedPartySize);

    const basePayload = {
      numOfAdults: Number(formData.numOfAdults) || 0,
      numOfChildren: Number(formData.numOfChildren) || 0,
      partySize: calculatedPartySize,
      bookingDate: formData.bookingDate,
      startTime: formData.startTime,
      endTime: resolvedEndTime,
      bookingDurationMinutes: calculatedBookingDurationMinutes,
      bookingPrice: Number(calculatedBookingPrice.toFixed(2)),
      notes: formData.notes?.trim() || "",
      serviceMode: resolvedServiceMode,
      locationNotes:
        resolvedServiceMode === SERVICE_MODE.MOBILE
          ? formData.locationNotes?.trim() || ""
          : "",
      onsiteAddress:
        resolvedServiceMode === SERVICE_MODE.MOBILE
          ? {
              line1: formData.onsiteAddress.line1?.trim() || "",
              line2: formData.onsiteAddress.line2?.trim() || "",
              city: formData.onsiteAddress.city?.trim() || "",
              state: formData.onsiteAddress.state?.trim() || "",
              zip: formData.onsiteAddress.zip?.trim() || "",
            }
          : null,
      service: {
        id: Number(formData.serviceId),
        hennaType: formData.hennaType,
      },
    };

    if (bookingOwnerType === BOOKING_OWNER_TYPE.GUEST) {
      return {
        ...basePayload,
        guest: {
          firstName: formData.guest.firstName?.trim() || "",
          lastName: formData.guest.lastName?.trim() || "",
          email: formData.guest.email?.trim() || "",
          phoneNumber: formData.guest.phoneNumber?.trim() || "",
        },
      };
    }

    return basePayload;
  };

  const runLocalValidation = () => {
    const resolvedServiceMode = getAutoServiceMode(calculatedPartySize);

    const validation = validateBookingPayload({
      bookingDate: formData.bookingDate,
      startTime: formData.startTime,
      endTime: formData.endTime,
      serviceId: formData.serviceId,
      hennaType: formData.hennaType,
      serviceMode: resolvedServiceMode,
      numOfAdults: formData.numOfAdults,
      numOfChildren: formData.numOfChildren,
      guest: bookingOwnerType === BOOKING_OWNER_TYPE.GUEST ? formData.guest : null,
      onsiteAddress: resolvedServiceMode === SERVICE_MODE.MOBILE ? formData.onsiteAddress : null,
    });

    setErrors(validation.errors || {});
    setFormData((prev) => ({
      ...prev,
      partySize: validation.partySize,
      serviceMode: getAutoServiceMode(validation.partySize),
    }));

    return validation;
  };

  const handleValidateAvailability = async () => {
    resetMessages();

    const validation = runLocalValidation();
    if (!validation.isValid) {
      setAvailabilityMessage("Please review the highlighted details before checking availability.");
      return;
    }

    try {
      setAvailabilityLoading(true);

      const payload = buildPayload();
      const data = await safeCallValidateAvailability({
        bookingDate: payload.bookingDate,
        startTime: payload.startTime,
        endTime: payload.endTime,
      });

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Availability validation failed.");
      }

      if (data?.available === true) {
        setAvailabilityMessage("Great news — your selected date and time are available.");
      } else {
        setAvailabilityMessage(
          data?.message || "That time is no longer available. Please choose another appointment time."
        );
      }
    } catch (err) {
      setAvailabilityMessage(err?.message || "We could not confirm that appointment time right now.");
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetMessages();

    const validation = runLocalValidation();
    if (!validation.isValid) {
      setPageError("Please review the form and correct the highlighted details before submitting.");
      return;
    }

    if (formData.hennaType === HENNA_TYPES.JAGUAR && formData.bookingDate) {
      const today = new Date();
      const bookingDate = new Date(`${formData.bookingDate}T00:00:00`);
      const minLeadTime = new Date(today);
      minLeadTime.setDate(today.getDate() + 14);

      if (bookingDate < minLeadTime) {
        setPageError(JAGUAR_HENNA_DISCLAIMER);
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        return;
      }
    }

    const payload = buildPayload();

    try {
      setSubmitting(true);

      const availabilityData = await safeCallValidateAvailability({
        bookingDate: payload.bookingDate,
        startTime: payload.startTime,
        endTime: payload.endTime,
      });

      if (availabilityData?.statusCode && availabilityData.statusCode !== 200) {
        throw new Error(availabilityData.message || "Availability validation failed.");
      }

      if (availabilityData?.available !== true) {
        throw new Error(
          availabilityData?.message || "The selected appointment time is no longer available."
        );
      }

      const data =
        bookingOwnerType === BOOKING_OWNER_TYPE.GUEST
          ? await safeCallCreateGuestBooking(payload)
          : await safeCallCreateUserBooking(payload);

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Booking request failed.");
      }

      const booked = data?.booking || null;
      const confirmationCode =
        data?.bookingConfirmationCode || booked?.bookingConfirmationCode || "";
      const guestEmail = payload?.guest?.email || "";
      const addressText = buildAddressText(payload.onsiteAddress);
      const modeDescription =
        payload.serviceMode === SERVICE_MODE.MOBILE
          ? MOBILE_MODE_DESCRIPTION
          : STUDIO_MODE_DESCRIPTION;

      navigate("/action-status", {
        replace: true,
        state: {
          status: "success",
          eyebrow: "Booking Confirmed",
          title: "Your appointment has been booked successfully",
          message:
            data?.message ||
            "Your henna appointment has been scheduled successfully.",
          note:
            payload.serviceMode === SERVICE_MODE.STUDIO
              ? "Please arrive on time for your appointment. Payment will be made in cash at the time of service. After your appointment is completed, henna aftercare tips will be sent to your email."
              : "Your mobile appointment has been scheduled successfully. Payment will be made in cash at the time of service, and the client is responsible for the artist’s travel expenses. After your appointment is completed, henna aftercare tips will be sent to your email.",
          details: [
            {
              label: "Confirmation Code",
              value: confirmationCode || "Will be provided by email",
            },
            {
              label: "Service",
              value: booked?.service?.serviceName || selectedService?.serviceName || "—",
            },
            {
              label: "Henna Type",
              value: payload.service?.hennaType || "—",
            },
            {
              label: "Date",
              value: payload.bookingDate ? formatDate(payload.bookingDate) : "—",
            },
            {
              label: "Time",
              value:
                payload.startTime && payload.endTime
                  ? `${formatTime(payload.startTime)} - ${formatTime(payload.endTime)}`
                  : "—",
            },
            {
              label: "Party Size",
              value: String(payload.partySize || 0),
            },
            {
              label: "Price",
              value: formatCurrency(payload.bookingPrice || 0),
            },
            {
              label: "Duration",
              value: `${payload.bookingDurationMinutes || 0} minutes`,
            },
            {
              label: "Appointment Type",
              value: modeDescription,
            },
            {
              label: payload.serviceMode === SERVICE_MODE.STUDIO ? "Studio Location" : "Appointment Address",
              value:
                payload.serviceMode === SERVICE_MODE.STUDIO
                  ? STUDIO_LOCATION
                  : addressText || payload.locationNotes || "—",
            },
            ...(payload.serviceMode === SERVICE_MODE.MOBILE
              ? [
                  {
                    label: "Travel Notice",
                    value: MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
                  },
                ]
              : []),
            ...(payload.service?.hennaType === HENNA_TYPES.JAGUAR
              ? [
                  {
                    label: "Jaguar Notice",
                    value: JAGUAR_HENNA_DISCLAIMER,
                  },
                ]
              : []),
            ...(payload.serviceMode === SERVICE_MODE.MOBILE && payload.locationNotes
              ? [
                  {
                    label: "Location Notes",
                    value: payload.locationNotes,
                  },
                ]
              : []),
            ...(guestEmail
              ? [
                  {
                    label: "Confirmation Sent To",
                    value: guestEmail,
                  },
                ]
              : []),
          ],
          actions: [
            {
              label: "Book Another Appointment",
              to: "/booking",
              variant: "primary",
            },
            {
              label: "Back to Home",
              to: "/",
              variant: "secondary",
            },
          ],
        },
      });
    } catch (err) {
      setPageError(err?.message || "We could not complete your booking.");
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingServices) {
    return <Loader fullScreen text="Preparing your booking experience..." />;
  }

  return (
    <div className="page-section">
      <PageContainer>
        <SectionTitle
          eyebrow="Book an Appointment"
          title="Book your henna service"
          subtitle="Choose your service, select an available date and time, and complete your appointment details with confidence."
          className="mb-8"
        />

        {pageError ? (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-700">{pageError}</p>
          </div>
        ) : null}

        {availabilityMessage ? (
          <div className="mb-6 rounded-2xl border border-[#d7c2a3] bg-[#faf6ef] p-4">
            <div className="flex items-start gap-3">
              <FiInfo className="mt-0.5 text-[#8B3A3A]" size={18} />
              <p className="text-sm text-[#3b2f2f]">{availabilityMessage}</p>
            </div>
          </div>
        ) : null}

        <div className="mb-6 content-card-lg p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-[#8B3A3A]">Booking Option</h2>
              <p className="mt-2 text-sm leading-6 text-muted-warm">
                {isAuthenticated
                  ? "You may continue as a signed-in client or switch to a guest booking if that feels easier for this appointment."
                  : "You are currently booking as a guest because no active sign-in session was found."}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {isAuthenticated && (
                <Button
                  variant={bookingOwnerType === BOOKING_OWNER_TYPE.USER ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => {
                    resetMessages();
                    setBookingOwnerType(BOOKING_OWNER_TYPE.USER);
                  }}
                >
                  Signed-In Client
                </Button>
              )}

              <Button
                variant={bookingOwnerType === BOOKING_OWNER_TYPE.GUEST ? "primary" : "secondary"}
                size="sm"
                onClick={() => {
                  resetMessages();
                  setBookingOwnerType(BOOKING_OWNER_TYPE.GUEST);
                }}
              >
                Guest Booking
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            <BookingForm
              formData={formData}
              errors={errors}
              services={services}
              selectedService={selectedService}
              calculatedPartySize={calculatedPartySize}
              calculatedBookingPrice={calculatedBookingPrice}
              calculatedBookingDurationMinutes={calculatedBookingDurationMinutes}
              onChange={handleChange}
              onSubmit={handleSubmit}
              onValidateAvailability={handleValidateAvailability}
              onSelectSlot={handleSelectSlot}
              availableDates={availableDates}
              availableSlots={availableSlots}
              slotsLoading={slotsLoading}
              loading={submitting}
              availabilityLoading={availabilityLoading}
              isGuest={bookingOwnerType === BOOKING_OWNER_TYPE.GUEST}
              submitLabel={
                bookingOwnerType === BOOKING_OWNER_TYPE.GUEST
                  ? "Submit Guest Booking"
                  : "Submit Booking"
              }
            />
          </div>

          <div className="min-w-0">
            <BookingSummary
              service={selectedService}
              bookingData={formData}
              calculatedPartySize={calculatedPartySize}
              calculatedBookingPrice={calculatedBookingPrice}
              calculatedBookingDurationMinutes={calculatedBookingDurationMinutes}
              className="sticky-summary"
            />
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default Booking;